<script>
import Titre from "@/components/atoms/titre";
import Button from "@/components/atoms/button";
import Icon from "@/components/atoms/icon";
import LazyImage from "@/components/atoms/lazy-image";
import FormGroup from "@/components/molecules/form-group";
import FormInput from "@/components/molecules/form-input";
import Badge from "@/components/atoms/badge";
import Form from "@/components/molecules/form";
import LinkTo from "@/components/atoms/link-to";
import RetrouverPorteur from "@/components/organisms/carte-fidelite-form/retrouver-porteur";
import Popin from "@/components/molecules/popin";
import { required } from "@vuelidate/validators";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import Captcha from "@/components/molecules/captcha";
import { dateIsMajeur } from "@/utils/form-validators";
import { useVuelidate } from "@vuelidate/core";

import carteMastercardEtMalin from "@/assets/images/carte-mastercard-et-carte-malin.png";

export default {
  name: "RecuperationNumeroCarte",
  components: {
    Titre,
    Button,
    Icon,
    LazyImage,
    FormInput,
    Badge,
    FormGroup,
    Form,
    RetrouverPorteur,
    Popin,
    Captcha
  },
  setup(props) {
    const { captcha, states, getState, getErrorMessage, checkValidation } =
      useFormValidation(props);

    const dateNaissance = ref("");
    const nom = ref("");
    const prenom = ref("");

    const validations = computed(() => ({
      nom: {
        required
      },
      prenom: {
        required
      },
      dateNaissance: {
        required,
        dateIsMajeur
      }
    }));
    const state = {};

    const v$ = useVuelidate(
      validations,
      { dateNaissance, nom, prenom },
      { $stopPropagation: true }
    );
    return {
      captcha,
      states,
      v$,
      dateNaissance,
      nom,
      prenom,
      getState,
      getErrorMessage,
      checkValidation,
      carteMastercardEtMalin
    };
  },
  props: {
    success: {
      type: Boolean,
      required: false,
      default: false
    },
    ...formValidationProps()
  },
  data() {
    return {
      captchaToken: null
    };
  },
  methods: {
    show() {
      this.$refs.popin.show();
    },
    hide() {
      this.$refs.popin.hide();
    },
    verifyCaptcha(token) {
      if (token) {
        this.captchaToken = token;
      }
      this.$emit("recuperationNumeroCarte", {
        dateNaissance: this.dateNaissance,
        nom: this.nom,
        prenom: this.prenom,
        "g-captcha-response": this.captchaToken
      });
    },
    recuperationNumeroCarte() {
      if (this.checkValidation(this.v$)) {
        this.$refs.captcha.execute();
      }
    }
  }
};
</script>

<template>
  <Popin
    ref="popin"
    class="u-text-center"
  >
    <template v-if="state === states.SUCCESS">
      <Badge
        variant="success"
        circle
        size="xlg"
        class="u-mb-sm"
      >
        <Icon
          name="check"
          variant="white"
        />
      </Badge>
      <Titre
        tag="h3"
        size="md"
        variant="primary"
        class="u-mb-lg"
      >
        C'est envoyé !
      </Titre>
      <p>Votre numéro de carte fidélité vient d’être envoyée dans votre boîte mail.</p>
      <Button
        radius="full"
        size="md"
        variant="primary"
        class="u-mt-lg"
        @click="$refs.popin.hide()"
      >
        Fermer
      </Button>
    </template>
    <template v-else-if="error">
      <Icon
        name="alert-circle-outline"
        size="lg"
        variant="alert"
        class="u-mb-md"
      />
      <template v-if="error.status_code === 404">
        <p>
          Désolé, nous ne pouvons pas récupérer votre numéro de carte fidélité.<br><br>
          Vous pouvez vous adresser au Village des services de votre magasin pour résoudre le problème.
        </p>
      </template>
      <template v-else>
        <p>
          Une erreur est survenue lors de la récupération de votre numéro de carte fidélité.<br><br>
          Nous vous invitons à réessayer ultérieurement.<br>
          Veuillez nous excuser pour la gêne occassionnée.
        </p>
      </template>
      <Button
        radius="full"
        size="md"
        variant="primary"
        class="u-mt-lg"
        @click="$refs.popin.hide()"
      >
        Fermer
      </Button>
    </template>
    <template v-else>
      <LazyImage
        :src="carteMastercardEtMalin"
        alt=""
        width="91"
        height="69"
        class="u-mb-xs25"
      />
      <Titre
        tag="h3"
        size="md"
        variant="primary"
        class="u-mb-lg"
      >
        Récupérer votre numéro de fidélité
      </Titre>
      <Form @submit.prevent="recuperationNumeroCarte">
        <div class="u-flex u-flex-column u-flex-align-center u-text-left">
          <div class="c-recuperation-numero-carte__content">
            <FormGroup
              class="u-mb-sm"
              size="md"
              :state="getState(v$, 'nom')"
            >
              <FormInput
                id="nom"
                v-model="nom"
                size="md"
                :state="getState(v$, 'nom')"
                @blur="v$.nom.$touch()"
                @input="v$.nom.$reset()"
              >
                Nom du porteur *
              </FormInput>
              <template #message>
                <p>
                  <template v-if="v$.nom.required.$invalid">
                    Merci de renseigner votre nom
                  </template>
                  <template v-else>
                    {{ getErrorMessage("nom") }}
                  </template>
                </p>
              </template>
            </FormGroup>
            <FormGroup
              :state="getState(v$, 'prenom')"
              class="u-my-sm"
              size="md"
            >
              <FormInput
                id="prenom"
                v-model="prenom"
                size="md"
                :state="getState(v$, 'prenom')"
                @blur="v$.prenom.$touch()"
                @input="v$.prenom.$reset()"
              >
                Prénom du porteur *
              </FormInput>
              <template #message>
                <p>
                  <template v-if="v$.prenom.required.$invalid">
                    Merci de renseigner votre prénom
                  </template>
                  <template v-else>
                    {{ getErrorMessage("prenom") }}
                  </template>
                </p>
              </template>
            </FormGroup>
            <RetrouverPorteur />
            <FormGroup
              class="u-mt-sm"
              size="md"
              :state="getState(v$, 'dateNaissance')"
            >
              <FormInput
                v-model="dateNaissance"
                size="md"
                type="date"
                active
                :state="getState(v$,'dateNaissance')"
                aria-labelledby="dateNaissanceCarte"
                @blur="v$.dateNaissance.$touch()"
                @input="v$.dateNaissance.$reset()"
              >
                Date de naissance *
              </FormInput>
              <template #message>
                <div id="dateNaissanceCarte">
                  <template v-if="v$.dateNaissance.required.$invalid">
                    Merci de renseigner votre date de naissance
                  </template>
                  <p v-else>
                    {{ getErrorMessage("dateNaissance", "Le porteur de carte doit être une personne majeure") }}
                  </p>
                </div>
              </template>
            </FormGroup>
          </div>
        </div>
        <Captcha
          ref="captcha"
          @verify="verifyCaptcha"
        />
        <Button
          variant="primary"
          radius="full"
          size="md"
          class="u-mt-lg"
          value=" Valider ces informations"
          type="submit"
          :disabled="v$.$invalid"
        />
      </Form>
    </template>
  </Popin>
</template>
<style lang="scss" scoped>
@import "@/assets/styles/components/recuperation-numero-carte.scss";
</style>